import React, { useEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import env from 'env'

// Backstop for a frame that neither loads nor errors — a hung connection.
const LOAD_TIMEOUT_MS = 7000

// HubSpot serves meetings from regional shards: meetings.hubspot.com,
// meetings-na1.hubspot.com, meetings-eu1.hubspot.com, …
const MEETINGS_ORIGIN = /^https:\/\/meetings(-[a-z0-9]+)?\.hubspot\.com$/

const REQUIRED_FIELDS = ['first_name', 'last_name', 'email', 'phone']

export function buildEmbedUrl(base, contact = {}) {
  const url = new URL(base)
  url.searchParams.set('embed', 'true')
  // Returning visitors are hydrated from sessionData — save them the retyping
  if (contact.first_name) url.searchParams.set('firstname', contact.first_name)
  if (contact.last_name) url.searchParams.set('lastname', contact.last_name)
  if (contact.email) url.searchParams.set('email', contact.email)
  return url.toString()
}

export function isTrustedOrigin(origin, base) {
  if (MEETINGS_ORIGIN.test(origin)) return true
  try {
    return origin === new URL(base).origin
  } catch (err) {
    return false
  }
}

// HubSpot documents that this payload shape may change, so read it defensively
// and treat anything unreadable as a fallback case rather than throwing.
export function contactFromPayload(payload) {
  const contact = payload?.bookingResponse?.postResponse?.contact

  if (!contact) return null

  return {
    first_name: contact.firstName || '',
    last_name: contact.lastName || '',
    email: contact.email || '',
    phone: contact.phone || '',
  }
}

export function isCompleteContact(contact) {
  return !!contact && REQUIRED_FIELDS.every(field => contact[field])
}

export default function HubspotMeetingEmbed(props) {
  const { contact, posthog, onBooked, onIncomplete, onFailure } = props

  const [loaded, setLoaded] = useState(false)
  const frameRef = useRef(null)
  const failedRef = useRef(false)
  // Keep the listener stable while always calling the latest handlers
  const latest = useRef()
  latest.current = { posthog, onBooked, onIncomplete, onFailure }

  const baseUrl = env.HUBSPOT_MEETING_EMBED_URL
  const [embedUrl, urlError] = safeEmbedUrl(baseUrl, contact)

  const fail = (reason, extra) => {
    if (failedRef.current) return
    failedRef.current = true

    const { posthog, onFailure } = latest.current

    posthog?.capture('lead_meeting_embed_load_failed', { reason })
    Sentry.captureException(
      new Error(`HubSpot meeting embed failed to load (${reason})`),
      { extra: { reason, url: baseUrl, ...extra } }
    )
    onFailure?.(reason)
  }

  useEffect(() => {
    posthog?.capture('lead_meeting_embed_shown')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (urlError) {
      fail('invalid_url', { message: urlError.message })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlError])

  useEffect(() => {
    if (loaded || urlError) return

    const timer = setTimeout(() => fail('timeout'), LOAD_TIMEOUT_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, urlError])

  /*
   * The iframe cannot tell us whether it worked. Chrome fires `load` for its own
   * network-error page too, so a blocked or unresolvable host looks identical to
   * a successful embed — verified against a blackholed host, where `load` fired
   * and the frame stayed blank. Probe the host separately instead: an ad blocker,
   * a DNS block and a dead network all reject this request, while a reachable
   * host resolves with an opaque response.
   */
  useEffect(() => {
    // No fetch means no probe — leave the timeout as the only check rather than
    // failing an embed that may well be fine
    if (!embedUrl || typeof fetch !== 'function') return

    let cancelled = false
    fetch(embedUrl, { mode: 'no-cors', credentials: 'omit', redirect: 'follow' })
      .catch(err => {
        if (!cancelled) fail('unreachable', { message: err.message })
      })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedUrl])

  // `error` doesn't bubble, so React's onError never fires for an iframe —
  // these have to be attached to the element itself
  useEffect(() => {
    const frame = frameRef.current
    if (!frame) return

    const onLoad = () => setLoaded(true)
    const onError = () => fail('error')

    frame.addEventListener('load', onLoad)
    frame.addEventListener('error', onError)
    return () => {
      frame.removeEventListener('load', onLoad)
      frame.removeEventListener('error', onError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedUrl])

  useEffect(() => {
    const handleMessage = event => {
      if (!event.data?.meetingBookSucceeded) return
      // postMessage is not governed by CORS — validating the sender is on us
      if (!isTrustedOrigin(event.origin, baseUrl)) return

      const { posthog, onBooked, onIncomplete } = latest.current
      const payload = event.data.meetingsPayload
      const booked = contactFromPayload(payload)
      const meeting = {
        booked: true,
        booked_at: new Date().toISOString(),
        event_date: payload?.bookingResponse?.event?.dateString || null,
        meeting_slug: payload?.userSlug || null,
      }

      if (!booked) {
        // The contract moved. Send key names only — never the values.
        posthog?.capture('lead_meeting_embed_unrecognized_payload', {
          keys: Object.keys(payload || {}),
        })
        Sentry.captureException(
          new Error('HubSpot meeting embed payload missing contact'),
          { extra: { payload_keys: Object.keys(payload || {}), url: baseUrl } }
        )
        onIncomplete?.(null, meeting, 'unrecognized_payload')
        return
      }

      if (!isCompleteContact(booked)) {
        // Auto-submitting without a phone is a guaranteed 422 — let them finish by hand
        posthog?.capture('lead_meeting_embed_manual_fallback', {
          reason: 'incomplete_payload',
        })
        onIncomplete?.(booked, meeting, 'incomplete_payload')
        return
      }

      posthog?.capture('lead_meeting_embed_booked', {
        has_phone: !!booked.phone,
        event_date: meeting.event_date,
      })
      onBooked?.(booked, meeting)
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl])

  if (urlError) return null

  return (
    <div className="relative w-full">
      {!loaded && (
        <p className="absolute inset-0 flex items-center justify-center m-0 font-light text-gray-600">
          Loading available times&hellip;
        </p>
      )}
      <iframe
        ref={frameRef}
        title="Schedule a call"
        data-test-id="meeting-scheduler"
        src={embedUrl}
        className="w-full block border-0"
        style={{ height: '70vh', minHeight: '560px' }}
      />
    </div>
  )
}

function safeEmbedUrl(baseUrl, contact) {
  try {
    return [buildEmbedUrl(baseUrl, contact), null]
  } catch (err) {
    return [null, err]
  }
}
